import { AvailableTenantDtoAccess, type AvailableTenantDto } from '@/generated/api/models';

export interface GroupedTenants {
  members: AvailableTenantDto[];
  /** Tenants reachable through a data-sharing agreement, each carrying its `via` home provenance. */
  shared: AvailableTenantDto[];
}

function byNameThenId(a: AvailableTenantDto, b: AvailableTenantDto): number {
  const byName = a.name.localeCompare(b.name);
  if (byName !== 0) {
    return byName;
  }
  return a.id.localeCompare(b.id);
}

/** Sorting here keeps the switcher correct independently of the order the backend happens to return. */
export function groupTenants(tenants: AvailableTenantDto[]): GroupedTenants {
  const members = tenants.filter(tenant => tenant.access === AvailableTenantDtoAccess.member).sort(byNameThenId);
  const shared = tenants
    .filter(tenant => tenant.access === AvailableTenantDtoAccess['data-sharing'])
    .sort(byNameThenId);
  return { members, shared };
}

/**
 * A given `preferredTenantId` wins if still in the accessible set; otherwise a
 * native (member) tenant is preferred over a shared one, so the user starts in their own context.
 */
export function pickDefaultTenant(
  tenants: AvailableTenantDto[],
  preferredTenantId?: string,
): AvailableTenantDto | null {
  if (preferredTenantId !== undefined) {
    const preferred = tenants.find(tenant => tenant.id === preferredTenantId);
    if (preferred) {
      return preferred;
    }
  }
  const { members, shared } = groupTenants(tenants);
  return members[0] ?? shared[0] ?? null;
}
